import { Row, Typography } from '@tempmee/react';
import { OutlineChevronLeftIcon as ChevronLeftIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import type { KeyboardEvent, MouseEvent } from 'react';
import { Link } from 'react-router';

export interface IAuthBackLinkProps {
  /**
   * Destination route. Required when `onClick` is not provided.
   * When omitted, `onClick` must be supplied — the element renders as an
   * in-page button instead of a `<Link>`.
   */
  to?: string;
  /**
   * Optional click handler. When provided without `to`, the component
   * renders as an accessible button (no navigation). When provided alongside
   * `to`, the handler fires before the router navigates.
   */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  label?: string;
}

const Body = ({ label }: { label: string }) => (
  <>
    <ChevronLeftIcon size={24} color={colors.secondary[500]} />
    <Typography as="span" bold color={colors.secondary[500]} style={{ fontSize: 18 }}>
      {label}
    </Typography>
  </>
);

export const AuthBackLink = ({ to, onClick, label = 'Back' }: IAuthBackLinkProps) => {
  if (!to) {
    // Button mode — used when the back action is in-page state only
    // (e.g. moving between steps of a multi-step form).
    const handleClick = onClick ?? (() => undefined);
    const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick(e as unknown as MouseEvent<HTMLElement>);
      }
    };

    return (
      <Row
        as="span"
        align="center"
        role="button"
        tabIndex={0}
        data-testid="auth-back-link"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={{ gap: 2, cursor: 'pointer', display: 'inline-flex' }}
      >
        <Body label={label} />
      </Row>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid="auth-back-link"
      style={{ textDecoration: 'none', display: 'inline-block' }}
    >
      <Row as="span" align="center" style={{ gap: 2 }}>
        <Body label={label} />
      </Row>
    </Link>
  );
};
